import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Loader2, CheckCircle2, MessageCircle, Sparkles, ArrowRight } from 'lucide-react';
import { readUserContext } from '../lib/userContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SABRIL_AVATAR = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/xf03ot6r_sabril.png';

/**
 * SabrilCloserBubble — Real conversational AI sales agent for /prsnl.
 *
 * Sabril (via Claude Sonnet) qualifies the lead, reveals the matching offer
 * with explicit pricing, then asks for first_name + phone + preferred call slot.
 * The bot returns either:
 *   { type: 'message', content: '...' }        → normal chat turn
 *   { type: 'lead_saved', first_name, ... }    → conversation closed, lead saved
 */

const STORAGE_KEY = 'sabril_closer_session';
const HISTORY_KEY = 'sabril_closer_history';

const genSessionId = () =>
  `sabril-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const INTRO_MESSAGES_LEGACY = [
  "Salut 👋 Sabril.",
  "Avant qu'on parle offres et prix — je veux comprendre où tu en es. Dis-moi en deux mots : resto qui tourne déjà, projet d'ouverture, ou plusieurs établissements à structurer ?",
];

// Inline CTA buttons triggered when Sabril mentions a price/offer in his message.
// Each CTA scrolls to the pricing section AND auto-clicks the right pricing tab.
const CTA_CATALOG = {
  refonte:   { label: 'Voir l\'offre Refonte',    price: '6 700€',    pricingTab: 'refonte',   route: '/prsnl' },
  ouverture: { label: 'Voir l\'offre Ouverture',  price: '14 900€',   pricingTab: 'ouverture', route: '/prsnl' },
  groupe:    { label: 'Voir l\'offre Groupe',     price: '29 000€',   pricingTab: 'groupe',    route: '/prsnl' },
  '1day':    { label: 'Réserver le 1 Day',        price: '2 000€',    pricingTab: 'audit_day', route: '/prsnl' },
  app:       { label: 'Souscrire à l\'App',       price: '229€/mois', action: 'subscribe-app', route: '/app' },
  ebook:     { label: 'Acheter l\'Ebook',         price: '47€',       pricingTab: null,        route: '/prsnl#ebook-card' },
};

const detectCtas = (text) => {
  if (!text) return [];
  const t = text.toLowerCase();
  // Detection requires an ACTUAL PRICE marker so we don't false-positive on
  // questions ("projet d'ouverture, ouverture, ou autre chose ?") or generic mentions.
  const hasRefontePrice   = /6\s*700\s*€|6[.,]?700\s*€/.test(t);
  const hasOuverturePrice = /14\s*900\s*€|14[.,]?900\s*€/.test(t);
  const hasGroupePrice    = /29\s*000\s*€|29[.,]?000\s*€/.test(t);
  const has1DayPrice      = /(^|[^\d])2\s*000\s*€|2[.,]?000\s*€/.test(t);
  const hasAppPrice       = /229\s*€\s*\/?\s*mois|229\s*€(?!\d)/.test(t);
  const hasEbookPrice     = /47\s*€(?!\d)/.test(t);
  // Direct URL mention OR phrasing about the green card / "carte verte" / "scroll en bas" → strong ebook signal
  const hasEbookUrl       = /audit-resto\.fr\/ebook|\/ebook\b|carte verte|carte (47|verte)|scroll en bas/i.test(text);

  // Pricing must also have a proposal verb nearby ("c'est", "tarif", "prix",
  // "investissement", "tu mets", "compte", "à", "pour")
  const proposalCue = /(\bc[''']?est\b|tarif|prix|investiss|tu mets|tu paies|compte|forfait|on est sur|j'envoie|on parle de|à\s+\d|pour\s+\d|recommande|prends|commence)/i;
  const hasCue = proposalCue.test(t);

  const found = [];
  if (hasRefontePrice && hasCue) found.push('refonte');
  if (hasOuverturePrice && hasCue) found.push('ouverture');
  if (hasGroupePrice && hasCue) found.push('groupe');
  if (has1DayPrice && hasCue) found.push('1day');
  if (hasAppPrice && hasCue) found.push('app');
  if ((hasEbookPrice && hasCue) || hasEbookUrl) found.push('ebook');
  return [...new Set(found)].slice(0, 2);
};

const InlineCta = ({ offerKey, onAction }) => {
  const cta = CTA_CATALOG[offerKey];
  if (!cta) return null;
  const isEbook = offerKey === 'ebook';
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      onClick={() => onAction(offerKey)}
      className={`inline-flex items-center gap-2 mt-2 px-3.5 py-2 rounded-full text-white text-[12px] font-black tracking-wide hover:scale-[1.03] active:scale-[0.97] transition-transform ${
        isEbook
          ? 'bg-gradient-to-r from-[#10B981] to-[#059669] shadow-[0_6px_18px_rgba(16,185,129,0.45)]'
          : 'bg-gradient-to-r from-[#F05A1A] to-[#FF7A3D] shadow-[0_6px_18px_rgba(240,90,26,0.45)]'
      }`}
      data-testid={`sabril-cta-${offerKey}`}
    >
      <span>{isEbook ? '📖' : '💳'} {cta.label}</span>
      <span className="text-white/80 font-bold text-[11px]">· {cta.price}</span>
    </motion.button>
  );
};

// ───────────────────────────────────────────────────────────
// OfferQuizCard — 3-question inline quiz that recommends the right offer.
// Triggered from a chip "🎯 Quiz : trouve ton offre en 3 questions".
// No LLM call : 100% deterministic logic, fast, no token cost.
// After Q3 the result card shows the offer + a CTA that injects a
// natural-sounding user message back into the chat so Sabril resumes
// the conversation knowing what the prospect wants.
// ───────────────────────────────────────────────────────────
const QUIZ_QUESTIONS = [
  {
    id: 'status',
    label: 'Ton resto, il est où ?',
    options: [
      { key: 'tourne',   label: 'Il tourne déjà',         icon: '🍽️' },
      { key: 'ouverture', label: 'Projet d\'ouverture',   icon: '🔑' },
      { key: 'multi',    label: 'Plusieurs établissements', icon: '🏛️' },
    ],
  },
  {
    id: 'mode',
    label: 'Tu préfères comment je bosse avec toi ?',
    options: [
      { key: 'sur_place_court',  label: 'Que tu viennes vite, format court', icon: '⚡' },
      { key: 'distanciel',       label: '100% à distance sur 90j',           icon: '💻' },
      { key: 'sur_place_long',   label: 'Que tu viennes plusieurs fois',     icon: '✈️' },
      { key: 'continu',          label: 'Cadre permanent, sans limite',      icon: '👑' },
    ],
  },
  {
    id: 'goal',
    label: 'Et ton ambition là maintenant ?',
    options: [
      { key: 'declic',         label: 'Un déclic chirurgical',          icon: '🎯' },
      { key: 'optimise',       label: 'Optimiser ce qui existe',         icon: '📈' },
      { key: 'transformation', label: 'Une transformation profonde',     icon: '🔥' },
      { key: 'scale',          label: 'Scaler / structurer un groupe',   icon: '🚀' },
    ],
  },
];

// Recommendation engine — returns { offer: 'economie'|'affaires'|'premiere'|'salon', label, reason, intent }
const recommendOffer = (answers) => {
  const { status, mode, goal } = answers || {};
  // Multi-resto → Groupe direct (sauf si veut du continu = Salon)
  if (status === 'multi') {
    if (mode === 'continu' || goal === 'scale') {
      return {
        offer: 'salon',
        label: 'Salon Privé · Direction de marque',
        price: '2 490€/mois',
        reason: 'Plusieurs établissements + cadre permanent = direction de marque externalisée mensuelle. Ça matche.',
        intent: 'salon',
        userMsg: 'Le quiz me dit Salon Privé. Je pilote plusieurs restos, je veux un cadre permanent.',
      };
    }
    return {
      offer: 'groupe',
      label: 'Accompagnement Groupe',
      price: 'Sur devis',
      reason: 'Multi-sites + besoin d\'uniformisation = mission Groupe. On structure tout en 6 mois.',
      intent: 'groupe',
      userMsg: 'Le quiz me dit Groupe. Je pilote plusieurs restos, je veux structurer tout ça.',
    };
  }
  // Projet d'ouverture → Première Classe (le format ouverture A à Z)
  if (status === 'ouverture') {
    return {
      offer: 'premiere',
      label: 'Première Classe · 17j Immersion',
      price: '14 900€',
      reason: 'Ouverture = format le plus dense. 17j sur place sur 90j pour tout poser : concept, carte, équipe, lancement.',
      intent: 'premiere',
      userMsg: 'Le quiz me dit Première Classe. J\'ai un projet d\'ouverture, je veux que tu viennes 17j sur 90j.',
    };
  }
  // Resto qui tourne déjà → on regarde mode + goal
  if (mode === 'sur_place_court' || goal === 'declic') {
    return {
      offer: 'economie',
      label: 'Classe Économie · 48h Express',
      price: '2 790€',
      reason: '48h chez toi, audit chirurgical, plan d\'action béton. Le déclic rapide sans engagement long.',
      intent: 'economie',
      userMsg: 'Le quiz me dit Économie 48h. Je veux un déclic rapide, sans engagement long.',
    };
  }
  if (mode === 'distanciel') {
    return {
      offer: 'affaires',
      label: 'Classe Affaires · 90j Distanciel',
      price: '7 990€',
      reason: '90 jours, 2 RDV Zoom/mois, WhatsApp prio. Tu pilotes, je te calibre à distance.',
      intent: 'affaires',
      userMsg: 'Le quiz me dit Affaires 90j distanciel. Je veux du pilotage stratégique sans présence physique.',
    };
  }
  if (mode === 'continu') {
    return {
      offer: 'salon',
      label: 'Salon Privé · Direction de marque',
      price: '2 490€/mois',
      reason: 'Cadre permanent, sans limite. Comme un directeur de marque externalisé.',
      intent: 'salon',
      userMsg: 'Le quiz me dit Salon Privé. Je veux un cadre permanent, sans limite de temps.',
    };
  }
  // Default fallback : transformation profonde → Première
  return {
    offer: 'premiere',
    label: 'Première Classe · 17j Immersion',
    price: '14 900€',
    reason: 'Transformation profonde = 17 jours d\'immersion + 90j de suivi. Le format le plus dense.',
    intent: 'premiere',
    userMsg: 'Le quiz me dit Première Classe. Je veux une transformation profonde avec toi sur le terrain.',
  };
};

const OFFER_PALETTE = {
  economie:  { from: '#10B981', to: '#047857', text: '#FFFFFF' },
  affaires:  { from: '#4F46E5', to: '#3730A3', text: '#FFFFFF' },
  premiere:  { from: '#D4A74C', to: '#8B6520', text: '#FFFFFF' },
  salon:     { from: '#1C1C1E', to: '#000000', text: '#F5F3F0' },
  groupe:    { from: '#7C2D12', to: '#431407', text: '#FFFFFF' },
};

const OfferQuizCard = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const handleAnswer = (questionId, optionKey) => {
    try { navigator.vibrate?.(12); } catch (_) { /* noop */ }
    const next = { ...answers, [questionId]: optionKey };
    setAnswers(next);
    if (step < QUIZ_QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Compute recommendation
      const rec = recommendOffer(next);
      setResult(rec);
    }
  };

  const handleConfirm = () => {
    try { navigator.vibrate?.(20); } catch (_) { /* noop */ }
    if (result && onComplete) onComplete(result);
  };

  if (result) {
    const pal = OFFER_PALETTE[result.offer] || OFFER_PALETTE.premiere;
    return (
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="mx-2 mb-3 rounded-2xl overflow-hidden shadow-[0_12px_36px_rgba(0,0,0,0.18)]"
        style={{
          background: `linear-gradient(160deg, ${pal.from} 0%, ${pal.to} 100%)`,
          color: pal.text,
        }}
        data-testid="sabril-quiz-result"
      >
        <div className="px-4 py-4">
          <p className="text-[10px] font-black uppercase tracking-[2.5px] opacity-70 mb-1.5">
            🎯 Recommandation du quiz
          </p>
          <p className="text-[18px] font-black leading-tight mb-1">{result.label}</p>
          <p className="text-[13px] font-bold opacity-90 mb-3">{result.price}</p>
          <p className="text-[13px] leading-snug opacity-95 mb-4">{result.reason}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-[13px] font-black tracking-tight active:scale-[0.97] transition-transform"
              style={{ color: pal.to }}
              data-testid="sabril-quiz-confirm"
            >
              <span>Top, je veux celle-là</span>
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.8} />
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-3.5 py-2.5 rounded-full text-[12px] font-bold opacity-80 hover:opacity-100 transition-opacity border"
              style={{ borderColor: 'rgba(255,255,255,0.35)' }}
              data-testid="sabril-quiz-restart"
            >
              Refaire
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const q = QUIZ_QUESTIONS[step];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="mx-2 mb-3 rounded-2xl overflow-hidden bg-white border border-black/5 shadow-[0_8px_24px_rgba(0,0,0,0.10)]"
      data-testid="sabril-quiz-card"
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#F05A1A]" strokeWidth={2.6} />
          <p className="text-[10px] font-black uppercase tracking-[2px] text-[#F05A1A]">
            Quiz · {step + 1}/{QUIZ_QUESTIONS.length}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] font-bold text-black/40 hover:text-black/70 transition-colors"
          data-testid="sabril-quiz-cancel"
          aria-label="Annuler le quiz"
        >
          Annuler
        </button>
      </div>
      {/* Progress bar */}
      <div className="px-4">
        <div className="h-[3px] rounded-full bg-black/5 overflow-hidden">
          <motion.div
            className="h-full bg-[#F05A1A]"
            initial={{ width: 0 }}
            animate={{ width: `${((step + 1) / QUIZ_QUESTIONS.length) * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>
      </div>
      <div className="px-4 pt-3 pb-4">
        <p className="text-[14.5px] font-black text-[#1a1a1a] leading-snug mb-3">
          {q.label}
        </p>
        <div className="flex flex-col gap-1.5">
          {q.options.map((opt) => (
            <motion.button
              key={opt.key}
              type="button"
              whileTap={{ scale: 0.97 }}
              onClick={() => handleAnswer(q.id, opt.key)}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#f5f3f0] hover:bg-[#ebe7e0] active:bg-[#dfd9cf] transition-colors text-left"
              data-testid={`sabril-quiz-option-${q.id}-${opt.key}`}
            >
              <span className="text-[16px] leading-none">{opt.icon}</span>
              <span className="text-[13px] font-bold text-[#1a1a1a] flex-1">{opt.label}</span>
              <ArrowRight className="w-3.5 h-3.5 text-black/40" strokeWidth={2.4} />
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};



const Bubble = ({ role, content, isTyping = false, isUnavailable = false, whatsappHref = null, onCtaClick = null }) => {
  const isUser = role === 'user';
  const ctas = !isUser && !isTyping && !isUnavailable ? detectCtas(content) : [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`}
      data-testid={`sabril-bubble-${role}${isUnavailable ? '-unavailable' : ''}`}
    >
      {!isUser && (
        <motion.img
          src={SABRIL_AVATAR}
          alt="Sabril"
          className="w-8 h-8 rounded-full mr-2 flex-shrink-0 mt-0.5 object-cover"
          style={{ objectPosition: '50% 18%' }}
          animate={isTyping ? { rotate: [-3, 3, -3], scale: [1, 1.03, 1] } : { rotate: 0, scale: 1 }}
          transition={isTyping ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <div className={`max-w-[82%] flex flex-col items-start ${isUser ? 'items-end' : ''}`}>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
            isUser
              ? 'bg-[#F05A1A] text-white rounded-br-sm'
              : 'bg-white text-[#1a1a1a] rounded-bl-sm shadow-sm border border-black/5'
          }`}
        >
          {isTyping ? (
            <span className="inline-flex gap-1 py-1" aria-label="Sabril écrit">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-[#1a1a1a]/40 animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : (
            // Markdown light: **bold** → <strong>
            <span dangerouslySetInnerHTML={{
              __html: String(content || '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
            }} />
          )}
        </div>
        {/* WhatsApp fallback button when bot is unavailable */}
        {isUnavailable && whatsappHref && (
          <motion.a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => { try { navigator.vibrate?.(25); } catch (_) {} }}
            className="inline-flex items-center gap-2 mt-2 px-4 py-2.5 rounded-full text-white text-[13px] font-black tracking-wide shadow-[0_8px_24px_rgba(37,211,102,0.45)]"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
            data-testid="sabril-whatsapp-fallback-btn"
          >
            <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
            <span>WhatsApp Sabril perso</span>
            <span className="text-white/80 font-bold text-[11px]">· réponse &lt; 1h</span>
          </motion.a>
        )}
        {ctas.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-0.5">
            {ctas.map((k) => (
              <InlineCta key={k} offerKey={k} onAction={onCtaClick} />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ─── Inline café booking flow — lives inside the chat as a step-by-step bubble
const CAFE_CITIES_FLOW = [
  { city: 'Marrakech', emoji: '🇲🇦', window: '1 → 15 juillet 2026',  date_range: ['2026-07-01', '2026-07-15'] },
  { city: 'Marbella',  emoji: '🇪🇸', window: '15 → 22 juillet 2026', date_range: ['2026-07-15', '2026-07-22'] },
  { city: 'Cannes',    emoji: '🇫🇷', window: '22 → 30 juillet 2026', date_range: ['2026-07-22', '2026-07-30'] },
  { city: 'Marrakech', emoji: '🇲🇦', window: '1 → 15 août 2026',     date_range: ['2026-08-01', '2026-08-15'], variant: '-aug' },
  { city: 'Paris',     emoji: '🇫🇷', window: '15 → 30 août 2026',    date_range: ['2026-08-15', '2026-08-30'] },
  { city: 'Miami',     emoji: '🇺🇸', window: '1 → 15 septembre 2026', date_range: ['2026-09-01', '2026-09-15'] },
];

const CAFE_TIME_SLOTS = [
  { label: 'Matin · 9h-11h',     value: '09:00-11:00' },
  { label: 'Midi · 12h-14h',     value: '12:00-14:00' },
  { label: 'Après-midi · 15h-17h', value: '15:00-17:00' },
  { label: 'Soir · 18h-20h',     value: '18:00-20:00' },
];

function InlineCafeFlow({ initialForm = null, onCancel, onSuccess }) {
  const DEFAULTS = { city: '', window: '', date: '', time_slot: '', first_name: '', last_name: '', phone: '', restaurant_name: '', _cityKey: '' };
  const [step, setStep] = useState(initialForm?.step || 'city');
  const [form, setForm] = useState({ ...DEFAULTS, ...(initialForm?.form || {}) });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const cityData = CAFE_CITIES_FLOW.find((c) => `${c.city}${c.variant || ''}` === form._cityKey);
  const dateMin = cityData?.date_range?.[0];
  const dateMax = cityData?.date_range?.[1];

  const pick = (patch) => setForm((s) => ({ ...s, ...patch }));
  const goto = (s) => setStep(s);

  const submit = async () => {
    setErrMsg('');
    if (!form.first_name || !form.last_name || !form.phone || !form.restaurant_name || !form.city || !form.date) {
      setErrMsg('Il manque un truc — vérifie tes infos.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/cafe-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          restaurant_name: form.restaurant_name,
          city: form.city,
          date: form.date,
          time_slot: form.time_slot,
          message: '',
          source: 'prsnl-inline-chat-cafe',
        }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      onSuccess({
        first_name: form.first_name,
        city: form.city,
        date: form.date,
        time_slot: form.time_slot,
      });
    } catch (e) {
      setErrMsg('Erreur — réessaye dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  const pillBtn = (label, onClick, active = false, testid = '') => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className="active:scale-[0.96] transition-transform"
      style={{
        background: active ? 'linear-gradient(135deg,#F05A1A 0%,#FF7A3D 100%)' : '#FFFFFF',
        color: active ? '#FFFFFF' : '#0B141A',
        padding: '8px 12px', borderRadius: 999,
        fontSize: 12.5, fontWeight: active ? 800 : 600,
        border: active ? '0.5px solid rgba(240,90,26,0.55)' : '0.5px solid rgba(0,0,0,0.10)',
        boxShadow: active ? '0 4px 12px rgba(240,90,26,0.30)' : '0 1px 2px rgba(0,0,0,0.04)',
        whiteSpace: 'nowrap',
      }}
      data-testid={testid}
    >{label}</button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex justify-start mb-3"
      data-testid="sabril-inline-cafe-flow"
    >
      <img
        src={SABRIL_AVATAR}
        alt="Sabril"
        className="w-8 h-8 rounded-full mr-2 flex-shrink-0 mt-0.5 object-cover"
        style={{ objectPosition: '50% 18%' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <div
        className="max-w-[88%] rounded-2xl rounded-bl-sm bg-white border border-black/5 shadow-sm overflow-hidden"
      >
        {/* Header */}
        <div className="px-3.5 py-2.5 border-b border-black/5 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg,#FFF5EE 0%,#FFF 100%)' }}
        >
          <span className="text-[16px]">☕</span>
          <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif', fontSize: 12.5, fontWeight: 800, color: '#0B141A', letterSpacing: '-0.005em' }}>
            Programme ton café avec Sabril
          </p>
          <button type="button" onClick={onCancel} className="ml-auto text-[11px] text-black/40 hover:text-black/70" data-testid="cafe-flow-cancel">Annuler</button>
        </div>

        {/* Body — step-by-step */}
        <div className="px-3.5 py-3" style={{ minWidth: 260 }}>
          {step === 'city' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2.5 font-semibold">Tu veux qu&apos;on se voie où ?</p>
              <div className="flex flex-wrap gap-1.5">
                {CAFE_CITIES_FLOW.map((c) => pillBtn(
                  `${c.emoji} ${c.city} · ${c.window}`,
                  () => { pick({ city: c.city, window: c.window, _cityKey: `${c.city}${c.variant || ''}`, date: '', time_slot: '' }); goto('date'); },
                  false,
                  `cafe-flow-city-${c.city.toLowerCase()}${c.variant || ''}`,
                ))}
              </div>
            </>
          )}

          {step === 'date' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2 font-semibold">Quelle date te va ? <span className="text-black/50 font-normal">({form.window})</span></p>
              <input
                type="date"
                min={dateMin}
                max={dateMax}
                value={form.date}
                onChange={(e) => pick({ date: e.target.value })}
                className="w-full mb-2.5 px-3 py-2 rounded-lg bg-[#f5f3f0] border border-black/10 text-[14px] focus:outline-none focus:border-[#F05A1A]/50"
                style={{ fontSize: '16px' }}
                data-testid="cafe-flow-date-input"
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('city')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-city">← Ville</button>
                <button type="button" disabled={!form.date} onClick={() => goto('time')} className="ml-auto text-[12.5px] px-4 py-1.5 rounded-full bg-[#F05A1A] text-white font-bold disabled:opacity-40" data-testid="cafe-flow-date-next">Suivant →</button>
              </div>
            </>
          )}

          {step === 'time' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2.5 font-semibold">Tu préfères quel moment ?</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {CAFE_TIME_SLOTS.map((t) => pillBtn(
                  t.label,
                  () => { pick({ time_slot: t.value }); goto('name'); },
                  form.time_slot === t.value,
                  `cafe-flow-time-${t.value}`,
                ))}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('date')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-date">← Date</button>
                <button type="button" onClick={() => goto('name')} className="ml-auto text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-skip-time">Peu importe →</button>
              </div>
            </>
          )}

          {step === 'name' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2.5 font-semibold">Comment tu t&apos;appelles ?</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input type="text" placeholder="Prénom" value={form.first_name} onChange={(e) => pick({ first_name: e.target.value })} className="px-3 py-2 rounded-lg bg-[#f5f3f0] border border-black/10 text-[14px] focus:outline-none focus:border-[#F05A1A]/50" style={{ fontSize: '16px' }} data-testid="cafe-flow-first-name" />
                <input type="text" placeholder="Nom" value={form.last_name} onChange={(e) => pick({ last_name: e.target.value })} className="px-3 py-2 rounded-lg bg-[#f5f3f0] border border-black/10 text-[14px] focus:outline-none focus:border-[#F05A1A]/50" style={{ fontSize: '16px' }} data-testid="cafe-flow-last-name" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('time')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-time">← Moment</button>
                <button type="button" disabled={!form.first_name.trim() || !form.last_name.trim()} onClick={() => goto('phone')} className="ml-auto text-[12.5px] px-4 py-1.5 rounded-full bg-[#F05A1A] text-white font-bold disabled:opacity-40" data-testid="cafe-flow-name-next">Suivant →</button>
              </div>
            </>
          )}

          {step === 'phone' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2.5 font-semibold">Ton WhatsApp ?</p>
              <input type="tel" inputMode="tel" placeholder="+33 6 12 34 56 78" value={form.phone} onChange={(e) => pick({ phone: e.target.value })} className="w-full mb-2.5 px-3 py-2 rounded-lg bg-[#f5f3f0] border border-black/10 text-[14px] focus:outline-none focus:border-[#F05A1A]/50" style={{ fontSize: '16px' }} data-testid="cafe-flow-phone" />
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('name')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-name">← Nom</button>
                <button type="button" disabled={form.phone.trim().length < 6} onClick={() => goto('restaurant')} className="ml-auto text-[12.5px] px-4 py-1.5 rounded-full bg-[#F05A1A] text-white font-bold disabled:opacity-40" data-testid="cafe-flow-phone-next">Suivant →</button>
              </div>
            </>
          )}

          {step === 'restaurant' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2.5 font-semibold">Le nom de ton resto / projet ?</p>
              <input type="text" placeholder="Ex : Maison Avocado" value={form.restaurant_name} onChange={(e) => pick({ restaurant_name: e.target.value })} className="w-full mb-2.5 px-3 py-2 rounded-lg bg-[#f5f3f0] border border-black/10 text-[14px] focus:outline-none focus:border-[#F05A1A]/50" style={{ fontSize: '16px' }} data-testid="cafe-flow-restaurant" />
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('phone')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-phone">← WhatsApp</button>
                <button type="button" disabled={form.restaurant_name.trim().length < 1} onClick={() => goto('confirm')} className="ml-auto text-[12.5px] px-4 py-1.5 rounded-full bg-[#F05A1A] text-white font-bold disabled:opacity-40" data-testid="cafe-flow-resto-next">Voir le récap →</button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <p className="text-[12.5px] text-[#0B141A] mb-2 font-semibold">On valide ?</p>
              <div className="rounded-lg bg-[#FFF5EE] border border-[#F05A1A]/20 p-2.5 mb-2.5 text-[12.5px] leading-relaxed text-[#0B141A]">
                <div><strong>{form.first_name} {form.last_name}</strong> · {form.restaurant_name}</div>
                <div className="text-black/65">📍 {form.city} · 🗓 {form.date}{form.time_slot ? ` · 🕐 ${form.time_slot}` : ''}</div>
                <div className="text-black/65">📱 {form.phone}</div>
              </div>
              {errMsg && <p className="text-[11.5px] text-red-600 mb-2">{errMsg}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={() => goto('restaurant')} className="text-[12px] px-3 py-1.5 rounded-full border border-black/15 text-black/60" data-testid="cafe-flow-back-restaurant">← Modifier</button>
                <button type="button" disabled={submitting} onClick={submit} className="ml-auto text-[12.5px] px-4 py-1.5 rounded-full bg-[#10B981] text-white font-bold disabled:opacity-50 flex items-center gap-1.5" data-testid="cafe-flow-submit">
                  {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…</> : <>✓ Bloque mon café</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}



const SabrilCloserBubble = ({ page = 'prsnl', greeting = null }) => {
  // per chat phase. Up to ~8 chips per phase scroll horizontally so the user
  // never has to type a common question. Covers all 4 Salon Privé offers
  // (Économie 48h · Affaires 90j distanciel · Première 17j sur place · Salon mensuel).
  const getQuickReplies = (msgs) => {
    if (!msgs || msgs.length === 0) return [];
    const last = msgs[msgs.length - 1];
    if (!last || last.role !== 'assistant') return [];
    const rawTxt = last.content || '';
    const txt = rawTxt.toLowerCase();

    // FSM Phase 6 entry — let the user type their personal info, no chips
    if (/pr[ée]nom\s*\??/i.test(txt) || /nom\s+de\s+famille/i.test(txt) || /nom\s+de\s+ton\s+resto/i.test(txt) || /num[ée]ro\s+de\s+t[ée]l/i.test(txt) || /quand.*voie|date.*venue|aimerais.*voie/i.test(txt)) {
      return [];
    }

    // Bot just refused a call → continuation chips
    if (/pas de call/i.test(txt)) {
      return [
        'Ok pas de call, on continue ici',
        'Pourquoi pas de call ?',
        'Tu fais comment concrètement alors ?',
        'On peut s\'écrire WhatsApp directement ?',
      ];
    }

    // ─── Offer-specific intros (just opened from a card click) ───
    // Detect bot's intro for each offer via signature phrases + quote-the-offer
    if (/ebook 30 jours.*plan d['']ex[ée]cution|tu regardes l['']ebook/i.test(rawTxt)) {
      return [
        '📖 Acheter l\'Ebook · 47€',
        'Comment je télécharge après paiement ?',
        'C\'est quoi exactement les 7 leviers ?',
        'Je peux poser des questions après l\'avoir lu ?',
        'C\'est pour un projet ou un resto qui tourne ?',
        '☕ Programmer un café avec Sabril',
      ];
    }
    if (/classe [ée]conomie.*48h express|48h sur place.*audit/i.test(rawTxt)) {
      return [
        'Tu viens dans quelle ville ?',
        'Tu accompagnes après les 48h ?',
        'C\'est quoi le livrable concret après ?',
        'Pourquoi pas 100% distanciel ?',
        'Combien ça coûte si je dois te déplacer ?',
        '☕ Programmer un café avec Sabril',
      ];
    }
    if (/classe affaires.*90j distanciel|90 jours.*pilotage strat[ée]gique/i.test(rawTxt)) {
      return [
        'Comment se passent les 2 RDV/mois ?',
        'Tu bosses avec mes managers aussi ?',
        'C\'est quoi le scope d\'un mois type ?',
        'Tu garantis des résultats sur 90j ?',
        'Je peux upgrader vers Première après ?',
        '☕ Programmer un café avec Sabril',
      ];
    }
    if (/premi[èe]re classe.*17j immersion|17 jours.*immersion sur place/i.test(rawTxt)) {
      return [
        'Les 17 jours, c\'est en continu ou fractionné ?',
        'Tu refais aussi la DA / le menu ?',
        'C\'est quoi les 12 chantiers exactement ?',
        'Tu coaches mes équipes sur le terrain ?',
        'Quel est le ROI moyen sur tes clients ?',
        '☕ Programmer un café avec Sabril',
      ];
    }
    if (/salon priv[ée].*direction externalis|direction de marque externalis/i.test(rawTxt)) {
      return [
        'C\'est quoi vraiment "à demeure" ?',
        'Tu prends combien de groupes en parallèle ?',
        'Je peux résilier quand je veux ?',
        'C\'est quoi le CA minimum pour rentrer ?',
        'Il reste vraiment 3 places ?',
        '☕ Programmer un café avec Sabril',
      ];
    }

    // Bot just mentioned offer comparison / offer choice question
    if (/diff[ée]rence|quelle offre|laquelle|choisir/i.test(txt)) {
      return [
        '🎯 Quiz : trouve mon offre en 3 questions',
        '☕ Programmer un café avec Sabril',
        'C\'est quoi l\'Économie 48h exactement ?',
        'Affaires 90j distanciel ça donne quoi ?',
        'Première 17j sur place ça marche comment ?',
        'Salon Privé c\'est pour qui ?',
        'Tu travailles avec mon équipe ou juste moi ?',
      ];
    }

    // Bot mentioned a specific offer family → offer-specific drilldown chips
    if (/économie|economie|48h|48 h|audit express/i.test(txt) && !/d[ée]j[àa]\s+r[ée]pondu/i.test(txt)) {
      return [
        'Tu fais quoi pendant ces 48h ?',
        'Le plan d\'action c\'est combien de pages ?',
        'Le débrief J+30 c\'est inclus ?',
        'Je peux upgrader vers Première après ?',
        'Tu viens seul ou avec quelqu\'un ?',
        'Tarif Économie ?',
      ];
    }
    if (/affaires|distanciel|2 rdv|2\s+rdv\s+par\s+mois/i.test(txt)) {
      return [
        'C\'est 100% à distance vraiment ?',
        'Les 2 RDV/mois durent combien ?',
        'Le WhatsApp prio c\'est H24 ?',
        'Tu livres quoi concrètement sur 90j ?',
        'Je peux mixer avec une visite sur place ?',
        'Tarif Affaires ?',
      ];
    }
    if (/premi[èe]re|17\s*j|17 jours|immersion/i.test(txt)) {
      return [
        '17j c\'est combien d\'allers-retours ?',
        'Tu bosses avec mes équipes en cuisine ?',
        'Tu refonds ma carte plat par plat ?',
        'Les fiches techniques c\'est inclus ?',
        'Le coaching dirigeant c\'est combien d\'heures ?',
        'Tarif Première ?',
      ];
    }
    if (/salon priv[ée]|2 490|2490/i.test(txt)) {
      return [
        'C\'est un engagement minimum ?',
        'Je peux arrêter quand je veux ?',
        'L\'App est vraiment incluse à vie ?',
        'Tu réponds en combien de temps ?',
        'Tarif Salon Privé ?',
      ];
    }

    // Bot revealed a price → closing chips + objections
    const hasPrice = /\d{2}\s?\d{3}\s?€/.test(rawTxt) || /\b\d{3,5}\s?€/.test(rawTxt) || /(refonte|ouverture|premi[èe]re|salon priv[ée]|économie|affaires).{0,40}(€|tarif)/i.test(rawTxt);
    if (hasPrice) {
      return [
        '✅ Ok ça marche, on bloque',
        '☕ Programmer un café avec Sabril',
        'Je peux payer en 3x ?',
        'Tu garantis quoi exactement ?',
        'Et si ça marche pas ?',
        'Détaille ce qui est inclus',
        'Pourquoi ce tarif et pas moins ?',
        '🤔 Je veux réfléchir 24h',
      ];
    }

    // Bot mentioned planning/methodology
    if (/12 étapes|m[ée]thodologie|comment.*proc[èe]de|8 piliers/i.test(txt)) {
      return [
        'Donne-moi un exemple concret',
        'Combien de temps ça prend en vrai ?',
        'T\'as déjà fait ça pour un resto comme le mien ?',
        'Quels résultats les autres ont eu ?',
      ];
    }

    // Bot mentioned references / cases
    if (/maison avocado|bon|zebra|afri.k.n|palazzo|levantin|cas client/i.test(txt)) {
      return [
        'Tu peux me partager des chiffres ?',
        'Je peux contacter un de tes clients ?',
        'C\'est quoi le résultat le plus marquant ?',
        'Ils ont fait quelle offre eux ?',
      ];
    }

    const userMsgs = msgs.filter((m) => m.role === 'user').length;

    // Cold start — visitor hasn't engaged yet
    if (userMsgs === 0) {
      return [
        '🎯 Quiz : trouve mon offre en 3 questions',
        '☕ Programmer un café avec Sabril',
        'Comment je choisis l\'offre qu\'il me faut ?',
        'C\'est quoi la différence entre les 4 offres ?',
        'Pourquoi pas d\'appel téléphonique ?',
        'Comment se passe l\'accompagnement 90j ?',
        'Tu prends combien de clients par an ?',
        'Tu te déplaces où dans le monde ?',
      ];
    }

    // Early qualif (1-2 user msgs) — discovery questions
    if (userMsgs <= 2) {
      return [
        '🎯 Quiz : trouve mon offre en 3 questions',
        '☕ Programmer un café avec Sabril',
        'C\'est quoi Économie 48h vs Première 17j ?',
        'Affaires 100% distanciel ça marche vraiment ?',
        'Tu viens combien de jours sur place ?',
        'Tu bosses avec mes équipes ?',
        'Combien tu prends de restos par an ?',
        'C\'est quoi tes garanties ?',
        'T\'as des résultats à montrer ?',
      ];
    }

    // Mid qualif (3-5 user msgs) — operational deepening
    if (userMsgs <= 5) {
      return [
        '☕ Programmer un café avec Sabril',
        'Quelle offre tu me recommandes ?',
        'Comment se passe la 1ère semaine ?',
        'Tu refonds la carte avec moi ou seul ?',
        'Tu m\'aides à recruter aussi ?',
        'Combien tu factures pour me déplacer un add-on site ?',
        'C\'est combien si je paie en plusieurs fois ?',
        'On signe quoi pour démarrer ?',
      ];
    }

    // Late qualif — push to decision / close
    return [
      '☕ Programmer un café avec Sabril',
      'Ok donne-moi le tarif direct',
      'On signe quand ?',
      'Tu peux venir dans quelle ville ?',
      'C\'est quoi le délai pour démarrer ?',
      'Comment tu factures la TVA ?',
      'Tu factures via quelle société ?',
      'Je peux passer par ma société ?',
    ];
  };

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [leadSaved, setLeadSaved] = useState(null); // { first_name, best_slot, recommended_offer }
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [cafeFlow, setCafeFlow] = useState(null); // null | { step, form: {...} }
  const [cafeBooked, setCafeBooked] = useState(null); // { first_name, city, date, time_slot }
  const [activeIntent, setActiveIntent] = useState(null); // tracks offer-locked context for chip persistence

  // Magic chip label that triggers the inline quiz instead of sending the text as a message
  const QUIZ_CHIP_LABEL = '🎯 Quiz : trouve mon offre en 3 questions';
  const CAFE_CHIP_LABEL = '☕ Programmer un café avec Sabril';

  // WhatsApp direct fallback config — when LLM is unavailable
  const SABRIL_WHATSAPP_NUMBER = '33745522450';
  const SABRIL_WHATSAPP_HREF = (() => {
    const ctxMsg = (() => {
      try {
        const ctx = readUserContext();
        if (ctx && ctx.restoName) {
          return `Salut Sabril ! Je viens du chat sur audit-resto.fr — diagnostic fait pour ${ctx.restoName}${ctx.firstName ? `, je suis ${ctx.firstName}` : ''}. Le bot était indispo, du coup je te contacte direct.`;
        }
      } catch (_) { /* noop */ }
      return "Salut Sabril ! Je viens du chat sur audit-resto.fr — le bot était indispo, je te contacte direct.";
    })();
    return `https://wa.me/${SABRIL_WHATSAPP_NUMBER}?text=${encodeURIComponent(ctxMsg)}`;
  })();

  // Inline CTA click handler — opens the right offer or triggers SaaS subscription
  const handleCtaClick = async (offerKey) => {
    try { navigator.vibrate?.(20); } catch (_) { /* noop */ }
    // App SaaS — trigger Stripe subscription directly
    if (offerKey === 'app') {
      try {
        const restaurantName = window.prompt('Nom de ton restaurant pour la facturation ?', '') || '';
        const res = await fetch(`${API_URL}/api/app/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            restaurant_name: restaurantName,
          }),
        });
        const data = await res.json();
        if (data && data.url) {
          window.location.href = data.url;
          return;
        }
      } catch (_) { /* fall through to navigation */ }
    }
    // Other offers — navigate to the right page, scroll to pricing, auto-click pricing tab
    const cta = CTA_CATALOG[offerKey];
    if (!cta) return;
    // Ebook → close chat then scroll natively to the inline card (robust polling)
    if (offerKey === 'ebook') {
      if (window.location.pathname !== '/prsnl') {
        window.location.href = cta.route;
        return;
      }
      setOpen(false);
      // Poll until the card is stably in viewport (handles dynamic doc-height shifts)
      let attempts = 0;
      const tryScroll = () => {
        attempts += 1;
        const card = document.getElementById('ebook-card');
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const inView = rect.top >= 0 && rect.top <= 80;
        if (inView || attempts > 8) return;
        card.scrollIntoView({ behavior: 'instant', block: 'start' });
        setTimeout(tryScroll, 200);
      };
      setTimeout(tryScroll, 450);
      return;
    }
    const targetRoute = cta.route;
    const pricingTab = cta.pricingTab;
    const goAndScroll = () => {
      const tabsEl = document.querySelector('[data-testid="pricing-section-top"]');
      if (tabsEl) {
        const top = tabsEl.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      if (pricingTab) {
        setTimeout(() => {
          const tabBtn = document.querySelector(`[data-testid="pricing-tab-${pricingTab}"]`);
          if (tabBtn) tabBtn.click();
        }, 750);
      }
    };
    setOpen(false);
    setTimeout(() => {
      if (window.location.pathname !== targetRoute) {
        // Cross-page navigation — store the intent so the target page picks it up after mount
        try { sessionStorage.setItem('sabril_cta_target_tab', pricingTab || ''); } catch (_) {}
        window.location.href = targetRoute;
      } else {
        goAndScroll();
      }
    }, 240);
  };
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Restore session on mount — keys are page-scoped so each page has its own conversation
  useEffect(() => {
    const sKey = `${STORAGE_KEY}__${page}`;
    const hKey = `${HISTORY_KEY}__${page}`;
    let sid = '';
    try {
      sid = localStorage.getItem(sKey) || '';
      const hist = JSON.parse(localStorage.getItem(hKey) || '[]');
      if (Array.isArray(hist) && hist.length) setMessages(hist);
    } catch (e) { /* ignore */ }
    if (!sid) {
      sid = genSessionId();
      try { localStorage.setItem(sKey, sid); } catch (e) { /* ignore */ }
    }
    setSessionId(sid);
  }, [page]);

  // Persist history (page-scoped)
  useEffect(() => {
    try {
      localStorage.setItem(`${HISTORY_KEY}__${page}`, JSON.stringify(messages.slice(-30)));
    } catch (e) { /* ignore */ }
  }, [messages, page]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Auto-focus on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open]);

  // Per-page intro messages — ton chaleureux, pro mais accessible
  const DEFAULT_INTRO_BY_PAGE = {
    prsnl: [
      "Salut 👋 Sabril.",
      "Voilà le deal : on s'échange 5 à 10 min, je te pose 3-4 questions ciblées pour comprendre ton resto, et **à la fin** je te dis quelle offre est faite pour toi et le tarif. Pas avant, sinon ça veut rien dire.",
      "On commence : un resto qui tourne déjà, un projet d'ouverture, ou plusieurs établissements à structurer ?",
    ],
    offre: [
      "Salut 👋 Sabril.",
      "Le deal : 5 min ensemble, 2-3 questions, et **à la fin** je te dis si l'Ebook à 47€ est suffisant pour toi ou s'il te faut autre chose. Pas de blabla commercial.",
      "On y va : t'as un resto qui tourne, ou t'es en projet ?",
    ],
    app: [
      "Bonjour 👋 Sabril.",
      "Le principe : on parle 5-10 min, je comprends ton stack et ta volumétrie, et **à la fin** je te dis si l'App Audit Resto colle à ton besoin (ou pas) — avec le tarif net.",
      "Première question : tu pilotes combien d'établissements aujourd'hui, et avec quelle caisse ?",
    ],
    home: [
      "Salut 👋 Sabril.",
      "Le deal : 5 min de discussion, je te pose 2-3 questions, et **à la fin** je t'oriente sur ce qui colle vraiment à ton besoin (avec le tarif net).",
      "On y va : tu cherches à structurer un resto qui tourne, monter une ouverture, l'app de pilotage, ou découvrir ma méthode ?",
    ],
  };

  // Contextual intros — when user clicks "Discuter" on a specific offer card,
  // Sabril DIRECTLY EXPLAINS the offer in detail (no premature qualif).
  // Then he asks if they have questions, with offer-specific quick-replies.
  const intentIntro = (intent) => {
    switch (intent) {
      case 'ebook':
        return `Salut 👋 Tu regardes l'**Ebook 30 jours** — mon plan d'exécution pour structurer un resto en 30 jours.\n\n**Ce que tu reçois :**\n📖 87 pages PDF · téléchargement immédiat\n🎯 7 leviers de rentabilité (food cost, menu, prix, équipe, acquisition, fidélité, pilotage)\n📋 Fiches techniques + cockpit Excel prêt à l'emploi\n🗓 30 jours d'actions concrètes (1 action/jour)\n\n**Pour qui c'est :** patrons sans budget pour du coaching, qui veulent un cap clair et exécuter seuls.\n\n**Tarif :** 47€ unique. Pas d'abonnement. Téléchargeable directement sur la page.\n\nTu as des questions sur le contenu, ou tu veux qu'on en discute ?`;
      case 'audit_day':
      case '1day':
      case 'economie':
        return `Salut 👋 Tu regardes la **Classe Économie · 48h Express** — mon format le plus tactique pour un audit ponctuel.\n\n**Ce qu'on fait ensemble :**\n📍 48h sur place dans ton resto (je viens chez toi)\n🔍 Audit 360° : rentabilité, menu & offre, Uber Eats / Glovo, acquisition\n📋 Plan d'action chiffré · livrables à J+5\n📞 90 jours de suivi WhatsApp pour appliquer\n\n**Pour qui c'est :** resto qui tourne déjà · CA ≥ 50 k€/mois · veut un audit honnête sans s'engager 90 jours.\n\n**Tarif :** à partir de 2 000€ — discuté en privé selon ton volume et ta ville.\n\nTu as des questions, ou tu veux qu'on en discute pour bloquer une date ?`;
      case 'affaires':
        return `Salut 👋 Tu regardes la **Classe Affaires · 90j Distanciel** — mon format pour une refonte en profondeur sans déplacement.\n\n**Ce qu'on fait ensemble :**\n🎯 90 jours de pilotage stratégique 100% distanciel\n📞 2 RDV de 90 min par mois (Zoom/Meet)\n💬 WhatsApp prioritaire entre les RDV\n📊 Plan personnalisé · revue mensuelle des KPI\n👥 Travail avec toi + tes managers clés\n\n**Pour qui c'est :** resto en activité qui veut refondre stratégie & rentabilité sans qu'on bouge sur place. CA ≥ 50 k€/mois.\n\n**Tarif :** à partir de 4 900€ — discuté en privé selon scope et nombre de sites.\n\nTu as des questions sur le format, ou tu veux qu'on en discute ?`;
      case 'premiere':
        return `Salut 👋 Tu regardes la **Première Classe · 17j Immersion** — mon offre signature pour transformer un resto en marque.\n\n**Ce qu'on fait ensemble :**\n🏗 **17 jours d'immersion sur place** (par tranches de 3-5j)\n🎨 Refonte ADN · concept · menu · expérience client\n👥 Coaching équipe sur le terrain (tu retrouves un patron solide)\n📈 90 jours de suivi WhatsApp + revue mensuelle\n🚀 12 chantiers exécutés (food cost, DA, marketing, opérations…)\n\n**Pour qui c'est :** ouverture qui veut frapper fort, ou resto qui veut devenir une vraie marque durable & virale. Budget ≥ 250 k€ (ouverture) ou CA ≥ 50 k€/mois (refonte).\n\n**Tarif :** à partir de 14 900€ — discuté en privé selon ton projet.\n\nTu as des questions sur les chantiers, ou tu veux qu'on en discute concrètement ?`;
      case 'salon':
        return `Salut 👋 Tu regardes le **Salon Privé · Direction externalisée** — mon cercle privé pour groupes & multi-sites.\n\n**Ce qu'on fait ensemble :**\n👑 Direction de marque externalisée · à demeure\n📞 Accès illimité WhatsApp 24/7\n🧠 Stratégie continue · pas de scope limité\n📊 Suivi mensuel des KPI + revue de comité\n🎯 Disponibilité prioritaire pour tes urgences\n\n**Pour qui c'est :** groupes ou multi-sites qui veulent un cerveau marketing externe permanent, sans recruter un Directeur Marque interne.\n\n**Tarif :** à partir de 2 490€/mois — sans engagement. 3 places restantes sur 13.\n\nTu as des questions sur le format, ou tu veux qu'on en discute pour voir si t'as la place ?`;
      case 'refonte':
        return `Bien vu 🤝 tu cherches une **Refonte**. J'ai 2 formats selon ton mode de travail :\n\n• **Classe Affaires · 90j distanciel** (à partir de 4 900€) — 2 RDV/mois + WhatsApp prio\n• **Première Classe · 17j immersion** (à partir de 14 900€) — je viens chez toi, on bosse sur le terrain\n\nT'es à combien de CA mensuel et tu préfères qu'on bosse à distance ou que je vienne sur place ?`;
      case 'ouverture':
        return `Bien vu 🔥 tu prépares une **Ouverture**. Mon offre signature pour ça c'est la **Première Classe · 17j Immersion** (à partir de 14 900€) — on construit l'ADN, le concept, le menu, l'expérience, on lance ensemble.\n\nT'as quel budget total pour le projet (travaux + équipement + fonds de roulement) ?`;
      case 'groupe':
        return `Bien vu 🤝 tu pilotes un **groupe**. Mon format dédié c'est le **Salon Privé · Direction externalisée** (à partir de 2 490€/mois). Direction de marque à demeure, accès 24/7, sans engagement.\n\nTu pilotes combien d'établissements aujourd'hui et quel est ton CA mensuel cumulé ?`;
      case 'app':
        return `Salut 👋 Tu regardes l'**Audit Resto App** — notre suite SaaS avec 6 agents IA dédiés (Théo finance, Sophie RH, Léo cuisine, Manu recrutement, Clara réseaux, et moi sur la stratégie).\n\n229€/mois · sans engagement.\n\nTu pilotes combien d'établissements aujourd'hui ?`;
      case 'question':
        return "Salut 👋 Sabril.\n\nVas-y, pose ta question — je te réponds direct, sans détour.";
      case 'project':
        return `Salut 👋 Sabril.\n\nRaconte-moi en deux lignes : ton projet, où tu en es, et ce que tu cherches à régler.`;
      default:
        return null;
    }
  };

  // First message when chat opens fresh
  useEffect(() => {
    if (open && messages.length === 0 && !sending) {
      // If the user already has audit data, greet them by name + reference their resto
      const ctx = (() => { try { return readUserContext(); } catch (_) { return null; } })();
      let intro;
      if (greeting) {
        intro = greeting;
      } else if (ctx && ctx.restoName) {
        const fname = ctx.firstName ? `${ctx.firstName} ` : '';
        const projectStatus = ctx.projectStatus;
        let openerLine;
        if (projectStatus === 'en_ouverture') {
          openerLine = `J'ai vu ton diagnostic pour « ${ctx.restoName} » — tu prépares l'ouverture. Bien.`;
        } else if (projectStatus === 'multi_resto') {
          openerLine = `J'ai vu ton diagnostic pour « ${ctx.restoName} » — plusieurs établissements à structurer, c'est ton enjeu.`;
        } else if (ctx.foodCost && ctx.foodCost > 32) {
          openerLine = `J'ai vu ton diagnostic pour « ${ctx.restoName} » — food cost à ${ctx.foodCost}%, c'est exactement ce qu'on règle.`;
        } else if (ctx.ca) {
          openerLine = `J'ai vu ton diagnostic pour « ${ctx.restoName} » — ${Number(ctx.ca).toLocaleString('fr-FR')}€/mois de CA, on a la base pour bosser sérieux.`;
        } else {
          openerLine = `J'ai vu ton diagnostic pour « ${ctx.restoName} ».`;
        }
        intro = `Salut ${fname}👋 Sabril.\n\n${openerLine}\n\nLe deal : 5-10 min de discussion, 2-3 questions ciblées pour valider l'angle, et **à la fin** je te confirme l'offre + le tarif net. Pas avant — sinon ça veut rien dire.\n\nOn commence : dis-moi juste ce qui te bloque concrètement aujourd'hui.`;
      } else {
        intro = (DEFAULT_INTRO_BY_PAGE[page] || DEFAULT_INTRO_BY_PAGE.prsnl).join('\n\n');
      }
      setMessages([{ role: 'assistant', content: intro }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messages.length, sending, page]);

  // Listen for global "sabril:open" events fired by other CTAs on the page.
  // Payload: { intent: 'refonte'|'ouverture'|'groupe'|'1day'|'app'|'ebook'|'question'|'project' }
  useEffect(() => {
    const handler = (e) => {
      const intent = e?.detail?.intent;
      // Build context-aware intro and replace any old message — fresh start with the right framing
      const intro = intentIntro(intent) || greeting || (DEFAULT_INTRO_BY_PAGE[page] || DEFAULT_INTRO_BY_PAGE.prsnl).join('\n\n');
      setMessages([{ role: 'assistant', content: intro }]);
      // Generate a NEW session so the LLM history starts clean too
      const newSid = genSessionId();
      try { localStorage.setItem(`${STORAGE_KEY}__${page}`, newSid); } catch (_) { /* ignore */ }
      setSessionId(newSid);
      setLeadSaved(null);
      // Track offer-locked context for chip persistence
      const OFFER_INTENTS = ['ebook', 'economie', 'affaires', 'premiere', 'salon', 'refonte', 'ouverture', 'groupe', '1day', 'audit_day'];
      setActiveIntent(OFFER_INTENTS.includes(intent) ? intent : null);
      setOpen(true);
    };
    window.addEventListener('sabril:open', handler);
    return () => window.removeEventListener('sabril:open', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, greeting]);

  const sendMessage = async (text) => {
    const userMsg = text.trim();
    if (!userMsg || sending) return;
    // Subtle haptic feedback on supported devices (iPhone w/ Vibration API)
    try { navigator.vibrate?.(15); } catch (_) { /* ignore */ }
    setError('');
    setSending(true);
    const nextHistory = [...messages, { role: 'user', content: userMsg }];
    setMessages(nextHistory);
    setInput('');

    try {
      // Inject any previously collected audit context so Sabril doesn't re-ask
      const userCtx = (() => { try { return readUserContext(); } catch (_) { return null; } })();
      const body = JSON.stringify({
        session_id: sessionId,
        history: nextHistory.slice(0, -1).slice(-20),
        user_message: userMsg,
        page,
        user_context: userCtx || null,
      });
      // Robust call with 1 silent retry (network hiccups)
      const callOnce = async () => {
        const r = await fetch(`${API_URL}/api/sabril/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Erreur réseau');
        return d;
      };
      let data;
      try {
        data = await callOnce();
      } catch (firstErr) {
        console.warn('[Sabril chat] first attempt failed, retrying...', firstErr);
        await new Promise((r) => setTimeout(r, 1200));
        data = await callOnce();
      }
      if (data.type === 'lead_saved') {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.message },
        ]);
        setLeadSaved({
          first_name: data.first_name,
          best_slot: data.best_slot,
          recommended_offer: data.recommended_offer,
        });
        // Reset session so a new visit starts fresh (page-scoped)
        try {
          localStorage.removeItem(`${HISTORY_KEY}__${page}`);
          localStorage.removeItem(`${STORAGE_KEY}__${page}`);
        } catch (e) { /* ignore */ }
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.content }]);
      }
    } catch (e) {
      // Soft fallback after retry — keep the chat alive, do NOT push to WhatsApp
      console.error('[Sabril chat] backend error after retry:', e);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "Petit hoquet réseau de mon côté 🌐 — renvoie ton dernier message, je le reprends tout de suite.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-5 right-5 z-[60] flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-white shadow-[0_12px_30px_rgba(0,0,0,0.18)] border border-black/10 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(240,90,26,0.25)] transition-all"
            data-testid="sabril-closer-launcher"
            aria-label="Ouvrir le chat avec Sabril"
          >
            <div className="relative">
              <span
                className="w-10 h-10 rounded-full flex items-center justify-center ring-2 ring-[#F05A1A]/30"
                style={{ background: 'linear-gradient(135deg, #F05A1A 0%, #C8442B 100%)' }}
              >
                <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.4} />
              </span>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#10b981] border-2 border-white" aria-hidden="true" />
            </div>
            <div className="text-left leading-tight">
              <p className="text-[11px] font-black uppercase tracking-[2px] text-[#F05A1A]">
                Sabril · En ligne
              </p>
              <p className="text-[13px] font-bold text-[#1a1a1a]">
                Dis-moi tout
              </p>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 240, damping: 26 }}
            className="fixed z-[60] flex flex-col rounded-2xl overflow-hidden border border-black/10 shadow-[0_24px_60px_rgba(0,0,0,0.30)] bg-[#f5f3f0]
                       bottom-0 right-0 left-0 sm:bottom-5 sm:right-5 sm:left-auto
                       w-full h-[100dvh] max-h-[100dvh] sm:w-[380px] sm:h-[560px] sm:max-h-[78vh]"
            data-testid="sabril-closer-panel"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-[#1a1a1a] text-white">
              <div className="relative">
              <img
                src={SABRIL_AVATAR}
                alt="Sabril"
                className="w-9 h-9 rounded-full object-cover"
                style={{ objectPosition: '50% 18%' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10b981] border-2 border-[#1a1a1a]" />
              </div>
              <div className="flex-1 leading-tight">
                <p className="text-[14px] font-bold">Sabril</p>
                <p className="text-[10px] text-white/55 uppercase tracking-[2px]">
                  {page === 'app'
                    ? 'Fondateur · Audit Resto App'
                    : 'Fondateur PRSNL · Coaching premium'}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Fermer le chat"
                data-testid="sabril-closer-close"
              >
                <X className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>

            {/* Offer-locked badge — "Active call" iOS style */}
            {activeIntent && (() => {
              const INTENT_INFO = {
                ebook:     { label: 'Ebook 30 jours',     color: '#34D399' },
                economie:  { label: 'Classe Économie',    color: '#5EEAD4' },
                affaires:  { label: 'Classe Affaires',    color: '#A5B4FC' },
                premiere:  { label: 'Première Classe',    color: '#FCD34D' },
                salon:     { label: 'Salon Privé',        color: '#CBD5E1' },
                refonte:   { label: 'Refonte',            color: '#A5B4FC' },
                ouverture: { label: 'Ouverture',          color: '#FCD34D' },
                groupe:    { label: 'Groupe',             color: '#CBD5E1' },
                '1day':    { label: '1 Day Audit',        color: '#5EEAD4' },
                audit_day: { label: '1 Day Audit',        color: '#5EEAD4' },
              };
              const info = INTENT_INFO[activeIntent];
              if (!info) return null;
              return (
                <div
                  className="flex items-center justify-center gap-1.5 py-1.5 px-3"
                  style={{
                    background: `linear-gradient(180deg, ${info.color}1A 0%, ${info.color}08 100%)`,
                    borderBottom: `0.5px solid ${info.color}33`,
                  }}
                  data-testid="sabril-active-intent-badge"
                >
                  <span
                    style={{
                      width: 6, height: 6, borderRadius: 999,
                      background: info.color,
                      boxShadow: `0 0 0 3px ${info.color}30`,
                    }}
                  />
                  <p style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
                    color: '#1a1a1a',
                  }}>
                    Offre · <span style={{ color: '#0f0f0f', fontWeight: 800 }}>{info.label}</span>
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveIntent(null)}
                    className="ml-1 active:opacity-60 transition-opacity"
                    style={{
                      width: 14, height: 14, borderRadius: 999,
                      background: 'rgba(0,0,0,0.10)',
                      border: 'none', padding: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    aria-label="Quitter le contexte d'offre"
                    data-testid="sabril-active-intent-dismiss"
                  >
                    <X className="w-2 h-2" strokeWidth={3} style={{ color: 'rgba(0,0,0,0.55)' }} />
                  </button>
                </div>
              );
            })()}

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-4"
              style={{ background: 'linear-gradient(180deg, #f5f3f0 0%, #ebe7e0 100%)' }}
            >
              {messages.map((m, i) => {
                const isLastAssistant =
                  m.role === 'assistant' && i === messages.length - 1 && !sending && !leadSaved && !cafeFlow && !cafeBooked;
                let chips = isLastAssistant ? getQuickReplies(messages) : [];
                // Persist the café chip across all assistant turns when in offer-locked context
                if (isLastAssistant && activeIntent && !chips.includes(CAFE_CHIP_LABEL)) {
                  chips = [...chips, CAFE_CHIP_LABEL];
                }
                return (
                  <div key={i}>
                    <Bubble
                      role={m.role}
                      content={m.content}
                      isUnavailable={!!m.isUnavailable}
                      whatsappHref={SABRIL_WHATSAPP_HREF}
                      onCtaClick={handleCtaClick}
                    />
                    {chips.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.15 }}
                        className="flex flex-wrap gap-1.5 mb-3 ml-10 mr-2"
                        data-testid="sabril-quick-replies-inline"
                      >
                        {chips.map((chip, idx) => {
                          const isQuiz = chip === QUIZ_CHIP_LABEL;
                          const isCafe = chip === CAFE_CHIP_LABEL;
                          const accent = isCafe
                            ? 'linear-gradient(135deg,#10B981 0%,#059669 100%)'
                            : isQuiz
                              ? 'linear-gradient(135deg,#F05A1A 0%,#FF7A3D 100%)'
                              : '#FFFFFF';
                          const accentColor = (isCafe || isQuiz) ? '#FFFFFF' : '#0B141A';
                          const border = isCafe
                            ? '0.5px solid rgba(16,185,129,0.55)'
                            : isQuiz
                              ? '0.5px solid rgba(240,90,26,0.55)'
                              : '0.5px solid rgba(0,0,0,0.10)';
                          const shadow = isCafe
                            ? '0 4px 12px rgba(16,185,129,0.30)'
                            : isQuiz
                              ? '0 4px 12px rgba(240,90,26,0.30)'
                              : '0 1px 2px rgba(0,0,0,0.04)';
                          return (
                            <button
                              key={`${chip}-${idx}`}
                              type="button"
                              onClick={() => {
                                try { navigator.vibrate?.(15); } catch (_) { /* noop */ }
                                if (isQuiz) {
                                  setQuizOpen(true);
                                } else if (isCafe) {
                                  setCafeFlow({ step: 'city', form: {} });
                                } else {
                                  sendMessage(chip);
                                }
                              }}
                              className="active:scale-[0.96] transition-transform"
                              style={{
                                background: accent,
                                color: accentColor,
                                padding: '7px 11px',
                                borderRadius: 999,
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
                                fontSize: 12.5,
                                fontWeight: (isCafe || isQuiz) ? 800 : 600,
                                letterSpacing: '-0.005em',
                                border,
                                boxShadow: shadow,
                                whiteSpace: 'nowrap',
                              }}
                              data-testid={
                                isQuiz ? 'sabril-quick-reply-quiz'
                                  : isCafe ? 'sabril-quick-reply-cafe'
                                    : `sabril-quick-reply-${idx}`
                              }
                            >
                              {chip}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                );
              })}
              {sending && <Bubble role="assistant" isTyping />}
              {cafeFlow && !cafeBooked && (
                <InlineCafeFlow
                  initialForm={cafeFlow}
                  onCancel={() => setCafeFlow(null)}
                  onSuccess={(payload) => {
                    setCafeFlow(null);
                    setCafeBooked(payload);
                    setMessages((prev) => [
                      ...prev,
                      {
                        role: 'assistant',
                        content: `Parfait ${payload.first_name} ✅ Café bloqué à **${payload.city}** le **${payload.date}**${payload.time_slot ? ' · ' + payload.time_slot : ''}. Je te confirme l'adresse sur WhatsApp dans la journée. À très vite ☕`,
                      },
                    ]);
                  }}
                />
              )}
              {leadSaved && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 mx-2 rounded-2xl p-4 border-2 border-[#10b981]/40 bg-[#10b981]/10 flex items-start gap-3"
                  data-testid="sabril-closer-lead-saved"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#10b981] flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                  <div className="leading-snug">
                    <p className="text-[12px] font-black uppercase tracking-[2px] text-[#10b981] mb-1">
                      Visite confirmée
                    </p>
                    <p className="text-[13px] text-[#1a1a1a]">
                      {leadSaved.first_name}, on revient vers toi <strong>sous 48h max</strong> par WhatsApp avec la date précise et les vols. À très vite chez toi 🚀
                    </p>
                  </div>
                </motion.div>
              )}
              {error && (
                <p className="text-[12px] text-red-600 px-3 py-2 mt-2 rounded-lg bg-red-50 border border-red-200">
                  {error}
                </p>
              )}
              {/* Inline Quiz — recommends the right offer in 3 questions */}
              {quizOpen && !leadSaved && (
                <OfferQuizCard
                  onCancel={() => setQuizOpen(false)}
                  onComplete={(result) => {
                    setQuizOpen(false);
                    // Send a natural user message describing the chosen offer.
                    // The backend FSM/LLM will pick it up and continue qualifying.
                    sendMessage(result.userMsg);
                  }}
                />
              )}
            </div>

            {/* Quick-reply chips have moved INLINE — rendered right after the last assistant message */}

            {/* Input bar */}
            {!leadSaved && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage(input);
                }}
                className="flex items-center gap-2 px-3 py-3 border-t border-black/5 bg-white"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending}
                  placeholder="Réponds à Sabril…"
                  className="flex-1 rounded-full px-4 py-2.5 text-[16px] bg-[#f5f3f0] border border-black/10 focus:outline-none focus:border-[#F05A1A]/50 focus:bg-white transition-colors disabled:opacity-60"
                  style={{ fontSize: '16px' }}
                  data-testid="sabril-closer-input"
                  maxLength={500}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="sentences"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="w-10 h-10 rounded-full bg-[#F05A1A] hover:bg-[#FF6A2A] text-white flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="sabril-closer-send"
                  aria-label="Envoyer"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.4} />
                  ) : (
                    <Send className="w-4 h-4" strokeWidth={2.4} />
                  )}
                </button>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SabrilCloserBubble;

/**
 * Helper to open the Sabril bubble from any CTA with a pre-loaded intent.
 *   import { openSabril } from '../components/SabrilCloserBubble';
 *   <button onClick={() => openSabril('refonte')}>Discuter de mon projet Refonte</button>
 *
 * Valid intents: 'refonte' | 'ouverture' | 'groupe' | '1day' | 'audit_day' | 'app' | 'ebook' | 'question' | 'project'
 */
export const openSabril = (intent) => {
  try {
    window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent } }));
  } catch (e) {
    // SSR / non-window envs — noop
  }
};
